"use client";
import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, NavLogo, NavLogoKindMetadata } from "./assets";
import { IBottomItem, IBottomSubItem, ITopItem, SubmenuWidth } from "./types";

interface IMobileNavigationButtonProps {
	readonly mobileNavigationOpen: boolean;
	readonly onMobileNavigationButtonClick: () => void;
}
export const MobileNavigationButton: React.FC<IMobileNavigationButtonProps> = ({
	mobileNavigationOpen,
	onMobileNavigationButtonClick,
}) => {
	return (
		<nav id="mobile_navigation_button">
			<button
				className="epic-header-relative epic-header-w-6 epic-header-h-10 epic-header-text-[color:var(--epic-nav-mobile-navigation-button-color)]"
				onClick={onMobileNavigationButtonClick}
			>
				<span className="epic-header-sr-only">
					{mobileNavigationOpen ? "Close main menu" : "Open main menu"}
				</span>
				<div className="epic-header-absolute epic-header-w-5 epic-header-transform -epic-header-translate-x-1/2 -epic-header-translate-y-1/2 epic-header-left-1/2 epic-header-top-1/2">
					<span
						aria-hidden="true"
						className={
							"epic-header-block epic-header-absolute epic-header-h-0.5 epic-header-w-5 epic-header-bg-current epic-header-transform epic-header-transition epic-header-duration-500 epic-header-ease-in-out" +
							(mobileNavigationOpen ? " epic-header-rotate-45" : " -epic-header-translate-y-1.5")
						}
					></span>
					<span
						aria-hidden="true"
						className={
							"epic-header-block epic-header-absolute epic-header-h-0.5 epic-header-w-5 epic-header-bg-current epic-header-transform epic-header-transition epic-header-duration-500 epic-header-ease-in-out" +
							(mobileNavigationOpen ? " epic-header-opacity-0" : "")
						}
					></span>
					<span
						aria-hidden="true"
						className={
							"epic-header-block epic-header-absolute epic-header-h-0.5 epic-header-w-5 epic-header-bg-current epic-header-transform epic-header-transition epic-header-duration-500 epic-header-ease-in-out" +
							(mobileNavigationOpen ? " -epic-header-rotate-45" : " epic-header-translate-y-1.5")
						}
					></span>
				</div>
			</button>
		</nav>
	);
};

interface IBottomNavigationItemProps {
	readonly item: IBottomItem;
	readonly highlightForOpenPage?: boolean;
	readonly inMobilePopup?: boolean;
	readonly hoverUnderline: boolean;
}
export const BottomNavigationItem: React.FC<IBottomNavigationItemProps> = ({
	item,
	highlightForOpenPage,
	inMobilePopup,
	hoverUnderline,
}) => {
	const [currentPath, setCurrentPath] = useState("");

	useEffect(() => {
		setCurrentPath(typeof window !== "undefined" ? window.location.pathname : "");
	}, []);

	const highlight = itemNeedsHighlighting(
		!!highlightForOpenPage,
		currentPath,
		item.href,
		item.buttonStyle,
	);

	const image = inMobilePopup ? item.mobileImage : item.image;
	if (item.buttonStyle) {
		return (
			<div className={`${inMobilePopup ? "" : "pl-12"}`}>
				<a
					role="menuitem"
					key={`secondary_toolbar_item_${item.label}_${inMobilePopup ? "mobile" : "desktop"}`}
					id={`secondary_toolbar_item_${item.label.replaceAll(" ", "_")}_${
						inMobilePopup ? "mobile" : "desktop"
					}`}
					href={item.href || ""}
					title={item.tooltip}
					className={`epic-header-flex epic-header-flex-row epic-header-gap-2 epic-header-justify-center epic-header-items-center epic-header-font-semibold epic-header-rounded-full epic-header-h-[40px] epic-header-bg-[color:var(--epic-nav-bottom-bar-button-background-color)] epic-header-text-[color:var(--epic-nav-bottom-bar-button-text-color)] hover:epic-header-bg-[color:var(--epic-nav-bottom-bar-button-hover-background-color)] focus-within:epic-header-bg-[color:var(--epic-nav-bottom-bar-button-focus-background-color)]`}
					onMouseDown={e => e.preventDefault()}
				>
					{image ? (
						<div className="epic-header-relative epic-header-w-6 epic-header-aspect-square">
							<img
								src={image}
								alt=""
								className="epic-header-object-contain epic-header-w-full epic-header-h-full"
								style={{ objectFit: "contain" }}
								width={24}
								height={24}
							/>
						</div>
					) : null}
					<div className="epic-header-px-8 epic-header-py-4">{item.label}</div>
				</a>
			</div>
		);
	}
	return (
		<a
			role="menuitem"
			key={`secondary_toolbar_item_${item.label}_${inMobilePopup ? "mobile" : "desktop"}`}
			id={`secondary_toolbar_item_${item.label.replaceAll(" ", "_")}_${
				inMobilePopup ? "mobile" : "desktop"
			}`}
			href={item.href || ""}
			title={item.tooltip}
			className={`epic-header-relative epic-header-flex epic-header-px-4 epic-header-justify-center epic-header-flex-col epic-header-items-center epic-header-h-full ${
				highlight
					? "epic-header-text-white hover:epic-header-text-[color:var(--epic-nav-selected-hover-text-color)] focus-within:epic-header-text-[color:var(--epic-nav-selected-hover-text-color)] epic-header-bg-[color:var(--epic-nav-selected-bottom-bar-item-back-color)] hover:epic-header-bg-[color:var(--epic-nav-selected-bottom-bar-item-hover-back-color)] focus-within:epic-header-bg-[color:var(--epic-nav-selected-hover-logo-back-color)]"
					: `epic-header-text-[color:var(--epic-nav-text-color)] hover:epic-header-text-[color:var(--epic-nav-hover-text-color)] focus-within:epic-header-text-[color:var(--epic-nav-hover-text-color)] ${hoverUnderline ? "focus-within:epic-header-underline hover:epic-header-underline" : ""}`
			}`}
			onMouseDown={e => e.preventDefault()}
		>
			<div className="epic-header-flex epic-header-flex-row epic-header-gap-2">
				{image ? (
					<div className="epic-header-relative epic-header-w-6 epic-header-aspect-square">
						<img
							src={image}
							alt=""
							className="epic-header-object-contain epic-header-w-full epic-header-h-full"
							style={{ objectFit: "contain" }}
							width={24}
							height={24}
						/>
					</div>
				) : null}
				<div>{item.label}</div>
			</div>

			<div
				className={`${highlight ? "" : "epic-header-hidden"} epic-header-pointer-events-none epic-header-absolute epic-header-left-[20%] epic-header-bottom-3 epic-header-w-[60%] epic-header-border-b-2 epic-header-border-solid epic-header-border-b-[color:var(--epic-nav-selected-bottom-bar-underline-back-color)]`}
			/>
		</a>
	);
};
interface IBottomNavigationSubItemProps {
	readonly subItem: IBottomSubItem;
	readonly transparentBackground?: boolean;
	readonly onGroupKeyDownCallback?: (event: React.KeyboardEvent<HTMLElement>) => void;
	readonly onClickCallback?: (event: React.MouseEvent<HTMLElement>) => void;
	readonly highlightForOpenPage?: boolean;
	readonly className?: string;
	readonly inMobilePopup?: boolean;
	readonly hoverUnderline: boolean;
}
export const BottomNavigationSubItem: React.FC<IBottomNavigationSubItemProps> = ({
	subItem,
	transparentBackground,
	onGroupKeyDownCallback,
	onClickCallback,
	highlightForOpenPage,
	className,
	inMobilePopup,
	hoverUnderline,
}) => {
	const [currentPath, setCurrentPath] = useState("");

	useEffect(() => {
		setCurrentPath(typeof window !== "undefined" ? window.location.pathname : "");
	}, []);

	const highlight = itemNeedsHighlighting(
		highlightForOpenPage,
		currentPath,
		subItem.href,
		subItem.buttonStyle,
	);
	const image = inMobilePopup ? subItem.mobileImage : subItem.image;
	return (
		<a
			role="menuitem"
			tabIndex={0}
			key={`bottom_navigation_sub_item_${subItem.label.replaceAll(" ", "_")}_${
				inMobilePopup ? "mobile" : "desktop"
			}`}
			id={`bottom_navigation_sub_item_${subItem.label}_${inMobilePopup ? "mobile" : "desktop"}`}
			onKeyDown={onGroupKeyDownCallback}
			onClick={onClickCallback}
			href={subItem.href || ""}
			className={`${
				inMobilePopup ? "epic-header-py-1" : "epic-header-py-3"
			} bottom-navigation-sub-item epic-header-flex epic-header-flex-row ${className} epic-header-gap-2 epic-header-px-4 ${
				transparentBackground
					? ""
					: "epic-header-bg-[color:var(--epic-nav-bottom-bar-submenu-background-color)]"
			} ${
				highlight
					? "epic-header-h-full epic-header-text-white epic-header-bg-[color:var(--epic-nav-selected-bottom-bar-item-back-color)] hover:epic-header-bg-[color:var(--epic-nav-selected-bottom-bar-item-hover-back-color)] focus-within:epic-header-bg-[color:var(--epic-nav-selected-hover-logo-back-color)] hover:epic-header-text-[color:var(--epic-nav-selected-hover-text-color)] focus-within:epic-header-text-[color:var(--epic-nav-selected-hover-text-color)]"
					: `epic-header-text-[color:var(--epic-nav-text-color)] hover:epic-header-text-[color:var(--epic-nav-hover-text-color)] focus-within:epic-header-text-[color:var(--epic-nav-hover-text-color)] ${hoverUnderline ? "focus-within:epic-header-underline hover:epic-header-underline" : ""}`
			}`}
			title={subItem.tooltip}
			onMouseDown={e => e.preventDefault()}
		>
			{image ? (
				<div className="epic-header-relative epic-header-w-6 epic-header-aspect-square">
					<img
						src={image}
						alt=""
						className="epic-header-object-contain epic-header-w-full epic-header-h-full"
						style={{ objectFit: "contain" }}
						width={24}
						height={24}
					/>
				</div>
			) : null}
			<div>{subItem.label}</div>
		</a>
	);
};

function anyItemNeedsHighlighting(
	highlightForOpenPage: boolean | undefined,
	currentPagePath: string | undefined | null,
	subitemPaths: string[] | undefined,
	skipSubmenuHighlightIfMatchesOneOfThesePaths: string[] | undefined,
): boolean {
	if (!highlightForOpenPage || !subitemPaths) {
		return false;
	}
	if (skipSubmenuHighlightIfMatchesOneOfThesePaths) {
		if (
			skipSubmenuHighlightIfMatchesOneOfThesePaths.some(path =>
				itemNeedsHighlighting(true, currentPagePath, path, false),
			)
		) {
			return false;
		}
	}
	return subitemPaths.some(subitemPath => {
		return itemNeedsHighlighting(highlightForOpenPage, currentPagePath, subitemPath, false);
	});
}

interface IBottomNavigationSubMenuProps {
	readonly item: IBottomItem;
	readonly highlightForOpenPage?: boolean;
	readonly className?: string;
	readonly hoverUnderline: boolean;
}
export const BottomNavigationSubMenu: React.FC<IBottomNavigationSubMenuProps> = ({
	item,
	highlightForOpenPage,
	className,
	hoverUnderline,
}) => {
	const [currentPath, setCurrentPath] = useState("");

	useEffect(() => {
		setCurrentPath(typeof window !== "undefined" ? window.location.pathname : "");
	}, []);

	const highlight = anyItemNeedsHighlighting(
		highlightForOpenPage,
		currentPath,
		[...(item.subItems?.filter(item => item.href).map(item => item.href ?? "") || [])],
		item.skipSubmenuHighlightIfMatchesOneOfThesePaths,
	);
	const submenuId = "secondarytoolbar_submenu_" + item.label.replaceAll(" ", "_");
	const [submenuKeyPostfix, setSubmenuKeyPostfix] = useState(new Date().toString());
	const onGroupKeyDownCallback = useCallback(
		(event: React.KeyboardEvent<HTMLElement>) => {
			moveFocusInSubmenuWithUpDown(event, submenuId);
		},
		[submenuId],
	);
	const onClickCallback = useCallback(() => {
		setSubmenuKeyPostfix(new Date().toString());
	}, []);
	//https://stackoverflow.com/questions/53205951/reactjs-how-to-reload-a-component-onclick
	const onSubmenuParentMouseDownCallback = useCallback((event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
	}, []);
	const onSubmenuParentClickCallback = useCallback((event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
	}, []);
	const subMenuWidth = item.submenuWidth || SubmenuWidth.Large;
	return (
		<div
			id={submenuId}
			key={submenuId + "_" + submenuKeyPostfix}
			className="epic-header-inline-block epic-header-relative epic-header-h-full epic-header-bg-transparent epic-header-outline-none epic-header-group"
		>
			<div
				key={`secondary_toolbar_more_item_${item.label}`}
				id={`secondary_toolbar_more_item_${item.label.replaceAll(" ", "_")}`}
				role="menuitem"
				aria-haspopup="true"
				aria-expanded={true}
				onMouseDown={onSubmenuParentMouseDownCallback}
				onClick={onSubmenuParentClickCallback}
				tabIndex={0}
				title={item.tooltip}
				onKeyDown={onGroupKeyDownCallback}
				className={`epic-header-flex epic-header-items-center epic-header-gap-1 epic-header-px-4 epic-header-cursor-pointer epic-header-group epic-header-h-full ${
					highlight
						? "epic-header-text-white epic-header-bg-[color:var(--epic-nav-selected-bottom-bar-item-back-color)] group-hover:epic-header-bg-[color:var(--epic-nav-selected-hover-logo-back-color)] group-focus-within:epic-header-bg-[color:var(--epic-nav-selected-hover-logo-back-color)] group-hover:epic-header-text-[color:var(--epic-nav-selected-hover-text-color)] group-focus-within:epic-header-text-[color:var(--epic-nav-selected-hover-text-color)]"
						: "epic-header-text-[color:var(--epic-nav-text-color)] group-hover:epic-header-text-[color:var(--epic-nav-hover-text-color)] group-focus-within:epic-header-text-[color:var(--epic-nav-hover-text-color)]"
				}`}
			>
				<span>{item.label}</span>
				<ChevronDown
					tooltip=""
					pathClasses={`${
						highlight
							? "epic-header-fill-white"
							: "epic-header-fill-[color:var(--epic-nav-chevron-fill-color)] group-hover:epic-header-fill-[color:var(--epic-nav-chevron-hover-fill-color)] group-focus-within:epic-header-fill-[color:var(--epic-nav-chevron-hover-fill-color)]"
					}`}
					svgClasses="transition ease-in-out group-hover:epic-header-rotate-180 group-focus-within:epic-header-rotate-180"
				/>
			</div>
			<div
				role="menu"
				key={"secondarytoolbar_submenu_contents_" + item.label}
				id={"secondarytoolbar_submenu_contents_" + item.label.replaceAll(" ", "_")}
				className={`epic-header-absolute epic-header-left-[-4px] epic-header-z-10 epic-header-bg-transparent epic-header-transition epic-header-ease-in-out epic-header-delay-150 epic-header-origin-top epic-header-scale-y-0 group-hover:epic-header-scale-y-100 group-focus-within:epic-header-scale-y-100`}
			>
				<div
					className={`epic-header-bg-[color:var(--epic-nav-submenu-background-color)] epic-header-border-[color:var(--epic-nav-submenu-border-color)] epic-header-border epic-header-flex epic-header-flex-col epic-header-items-stretch epic-header-gap-[2px] epic-header-drop-shadow-2xl ${
						subMenuWidth === SubmenuWidth.Large
							? "epic-header-w-72"
							: subMenuWidth === SubmenuWidth.Medium
								? "epic-header-w-64"
								: "epic-header-w-40"
					}`}
				>
					{item.subItems
						? item.subItems
								.filter(({ allowedScreenSizeMode }) => {
									return (
										allowedScreenSizeMode === undefined ||
										allowedScreenSizeMode === "desktop" ||
										allowedScreenSizeMode === "any"
									);
								})
								.map(subItem => {
									return (
										<BottomNavigationSubItem
											key={`nav_bar_bottom_navigation_submenu_subitem_${item.label}_${subItem.label}`}
											onGroupKeyDownCallback={onGroupKeyDownCallback}
											onClickCallback={onClickCallback}
											subItem={subItem}
											highlightForOpenPage={
												highlightForOpenPage && subItem.allowhighlight !== false
											}
											className={className}
											hoverUnderline={hoverUnderline}
										/>
									);
								})
						: null}
				</div>
			</div>
		</div>
	);
};

interface ITopNavigationSubMenuProps {
	readonly item: ITopItem;
}
export const TopNavigationSubMenu: React.FC<ITopNavigationSubMenuProps> = ({ item }) => {
	const submenuId = "secondarytoolbar_submenu_" + item.navLogoKind;
	const onGroupKeyDownCallback = useCallback(
		(event: React.KeyboardEvent<HTMLElement>) => {
			moveFocusInSubmenuWithUpDown(event, submenuId);
		},
		[submenuId],
	);
	const onSubmenuParentMouseDownCallback = useCallback((event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
	}, []);
	const onSubmenuParentClickCallback = useCallback((event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
	}, []);
	const { LogoSvg, href } = NavLogoKindMetadata[item.navLogoKind];
	const submenuParent = (
		<li
			className="epic-header-flex epic-header-flex-col epic-header-justify-center epic-header-items-center"
			title={item.tooltip}
		>
			<a
				href={href}
				id={`nav_logo_${href}`}
				className={`epic-header-w-fit epic-header-flex-1 epic-header-px-3 epic-header-transition-[background-color] epic-header-duration-[400ms] epic-header-cursor-pointer epic-header-flex epic-header-flex-row epic-header-items-center epic-header-gap-1 epic-header-group/navlink ${
					item.selected
						? "epic-header-bg-[color:var(--epic-nav-selected-logo-back-color)] hover:epic-header-bg-[color:var(--epic-nav-selected-hover-logo-back-color)] focus-within:epic-header-bg-[color:var(--epic-nav-selected-hover-logo-back-color)]"
						: "hover:epic-header-border-white epic-header-rounded-sm hover:epic-header-bg-[color:var(--epic-nav-hover-logo-back-color)] focus-within:epic-header-bg-[color:var(--epic-nav-hover-logo-back-color)]"
				}`}
			>
				<LogoSvg
					tooltip={item.tooltip}
					pathClasses={`${
						item.selected
							? "epic-header-fill-[color:var(--epic-nav-selected-logo-fill-color)] group-hover/navlink:epic-header-fill-[color:var(--epic-nav-selected-hover-logo-fill-color)] group-focus-within/navlink:epic-header-fill-[color:var(--epic-nav-selected-hover-logo-fill-color)]"
							: "epic-header-fill-[color:var(--epic-nav-logo-fill-color)] group-hover/navlink:epic-header-fill-[color:var(--epic-nav-hover-logo-fill-color)] group-focus-within/navlink:epic-header-fill-[color:var(--epic-nav-hover-logo-fill-color)]"
					}`}
					big={false}
				/>
				<ChevronDown
					tooltip=""
					pathClasses={`${
						item.selected
							? "epic-header-fill-white"
							: "epic-header-fill-[color:var(--epic-nav-chevron-fill-color)] group-hover:epic-header-fill-[color:var(--epic-nav-chevron-hover-fill-color)] group-focus-within:epic-header-fill-[color:var(--epic-nav-chevron-hover-fill-color)]"
					}`}
					svgClasses="transition epic-header-ease-in-out group-hover:epic-header-rotate-180 group-focus-within:epic-header-rotate-180"
				/>
			</a>
		</li>
	);
	const subMenuWidth = item.submenuWidth || SubmenuWidth.Large;

	return (
		<div
			id={submenuId}
			key={submenuId}
			className="epic-header-inline-block epic-header-relative epic-header-h-full epic-header-bg-transparent epic-header-outline-none epic-header-group"
		>
			<div
				key={`primary_toolbar_more_item_${item.navLogoKind}`}
				id={`primary_toolbar_more_item_${item.navLogoKind}`}
				role="menuitem"
				aria-haspopup="true"
				aria-expanded={true}
				onMouseDown={onSubmenuParentMouseDownCallback}
				onClick={onSubmenuParentClickCallback}
				onKeyDown={onGroupKeyDownCallback}
				className={`epic-header-flex epic-header-items-center epic-header-gap-1 epic-header-cursor-pointer epic-header-group epic-header-h-full ${
					item.selected
						? "epic-header-bg-[color:var(--epic-nav-selected-logo-back-color)] group-hover:epic-headerbg-[color:var(--epic-nav-selected-hover-logo-back-color)] group-focus-within:epic-header-bg-[color:var(--epic-nav-selected-hover-logo-back-color)] epic-header-text-white group-hover:epic-header-text-[color:var(--epic-nav-selected-hover-text-color)] group-focus-within:epic-header-text-[color:var(--epic-nav-selected-hover-text-color)]"
						: "epic-header-text-[color:var(--epic-nav-text-color)] group-hover:epic-header-text-[color:var(--epic-nav-hover-text-color)] group-focus-within:epic-header-text-[color:var(--epic-nav-hover-text-color)]"
				}`}
			>
				{submenuParent}
			</div>
			<div
				role="menu"
				key={"primarytoolbar_submenu_contents_" + item.navLogoKind}
				id={"primarytoolbar_submenu_contents_" + item.navLogoKind}
				className={`epic-header-absolute epic-header-left-[-4px] epic-header-z-10 epic-header-bg-transparent epic-header-transition epic-header-ease-in-out epic-header-delay-150 epic-header-origin-top epic-header-scale-y-0 group-hover:epic-header-scale-y-100 group-focus-within:epic-header-scale-y-100`}
			>
				<div
					className={`epic-header-bg-[color:var(--epic-nav-submenu-background-color)] epic-header-border-[color:var(--epic-nav-submenu-border-color)] epic-header-border epic-header-flex epic-header-flex-col epic-header-items-stretch epic-header-gap-[2px] epic-header-drop-shadow-2xl ${
						subMenuWidth === SubmenuWidth.Large
							? "epic-header-w-72"
							: subMenuWidth === SubmenuWidth.Medium
								? "w-52"
								: "w-40"
					}`}
				>
					{item.subItems
						? item.subItems.map(subItem => {
								return (
									<div
										key={`nav_bar_bottom_navigation_submenu_subitem_${item.navLogoKind}`}
										className="epic-header-flex epic-header-flex-col epic-header-justify-center epic-header-items-center epic-header-h-16 epic-header-bg-white epic-header-grow-0 epic-header-shrink-0"
									>
										<NavLogo
											navLogoKind={subItem.navLogoKind}
											tooltip={subItem.tooltip}
											selected={subItem.selected}
											big
										/>
									</div>
								);
							})
						: null}
				</div>
			</div>
		</div>
	);
};

function moveFocusInSubmenuWithUpDown(
	event: React.KeyboardEvent<HTMLElement>,
	submenuId: string,
): void {
	// Arrow up
	if (event.code === "ArrowUp") {
		const activeElement = document.activeElement;
		const listItems = document.querySelector("#" + submenuId)?.childNodes[1]?.childNodes[0]
			?.childNodes;
		event.preventDefault();
		if (!listItems) {
			return;
		}
		for (let index = 1; index < listItems.length; index++) {
			if (activeElement === listItems[index]) {
				(listItems[index - 1] as HTMLElement)?.focus();
				return;
			}
		}
		(listItems[listItems.length - 1] as HTMLElement)?.focus();
	}

	// Arrow down
	else if (event.code === "ArrowDown") {
		const activeElement = document.activeElement;
		const listItems = document.querySelector("#" + submenuId)?.childNodes[1]?.childNodes[0]
			?.childNodes;
		if (!listItems) {
			return;
		}
		event.preventDefault();
		for (let index = 0; index < listItems.length - 1; index++) {
			if (activeElement === listItems[index]) {
				(listItems[index + 1] as HTMLElement)?.focus();
				return;
			}
		}
		(listItems[0] as HTMLElement)?.focus();
	}
}

function itemNeedsHighlighting(
	highlightForOpenPage: boolean | undefined,
	currentPagePath: string | undefined | null,
	thisItemPath: string | undefined | null,
	buttonStyle: boolean | undefined | null,
): boolean {
	if (!highlightForOpenPage || buttonStyle) {
		return false;
	}
	if (thisItemPath === "/") {
		return currentPagePath === "/";
	}
	const currentPagePathWithSlashesAtEnds = withSlashesAtEnds(currentPagePath || "");
	const thisItemPathWithSlashesAtEnds = withSlashesAtEnds(thisItemPath || "");
	return currentPagePathWithSlashesAtEnds.includes(thisItemPathWithSlashesAtEnds);
}

function withSlashesAtEnds(path: string): string {
	let pathWithSlashes = path;
	if (!pathWithSlashes.endsWith("/")) {
		pathWithSlashes += "/";
	}
	if (!pathWithSlashes.startsWith("/")) {
		pathWithSlashes = "/" + pathWithSlashes;
	}
	return pathWithSlashes;
}

